import React, {useState} from 'react';
import type {TableColumnsType} from 'antd';
import {Button, Table, Tag, Select, message} from 'antd';
import {CheckOutlined, CloseOutlined, EditOutlined} from '@ant-design/icons';
import {formatTimestampToDateTime} from "../../../../utils/dateTimeUtils";
import {useQuery, useMutation} from "@apollo/client";
import { allChannelResponse, GET_ALL_CHANNEL } from '../../../../graphql/queries/getChannelAll';
import {OrderWithAddressType} from "../hooks/useWmsExceptionQuery";
import {
    ExceptionDictionaryResponse,
    ExceptionDictionaryType,
    GET_ALL_EXCEPTION
} from "../graphql/queries/getAllException";
import {BATCH_SET_NEXT_ACTION, BatchSetNextActionResponse, BatchSetNextActionVariables} from "../graphql/mutations/setNextAction";
import { nextActionOptions, ACTION_NAME_MAP} from "../../../../config/filterOptions";
import { GET_HUB_LIST, GetHubListResponse } from 'graphql/queries/warehouseQueries';
import { SiteType } from 'graphql/types/SiteType';
import { GET_ALL_WAREHOUSES, WarehousesResponse} from '../graphql/queries/getAllWarehouse';
import { useTranslation } from 'react-i18next';
import { showError } from '../../../../utils/exceptionUtils';
import { useStatusInternationalization } from 'pages/delivery/parcels/hooks/useStatusInternationalization';

// Next Action 选项配置 (与后端 ExceptionAction 枚举匹配)
// 删除这个静态定义，改为在组件内部动态生成国际化的选项

interface WmsExceptionTableProps {
    parcelData: OrderWithAddressType[];
    showParcelDetail: (record: OrderWithAddressType) => void;
    onSelectChange?: (selectedRowKeys: React.Key[], selectedRows: OrderWithAddressType[]) => void;
    selectedRowKeys?: React.Key[];
    loading?: boolean;
    onRefresh?: () => void; // 刷新数据的回调
}

export const WmsExceptionTable: React.FC<WmsExceptionTableProps> = ({
    parcelData, 
    showParcelDetail, 
    onSelectChange,
    selectedRowKeys: externalSelectedRowKeys,
    loading,
    onRefresh
}) => {
    const { t } = useTranslation();
    const { getStatusText, getExceptionStatusText, getExceptionReasonText, getNextActionTextFromBackend, exceptionStatusMap } = useStatusInternationalization();
    
    const [internalSelectedRowKeys, setInternalSelectedRowKeys] = useState<React.Key[]>([]);
    const selectedRowKeys = externalSelectedRowKeys !== undefined ? externalSelectedRowKeys : internalSelectedRowKeys;
    const {data} = useQuery<allChannelResponse>(GET_ALL_CHANNEL);
    const {data:exceptionData} = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION);
    const exceptionList = exceptionData?.getExceptionDictionaryAll || [];
    // Next Action 编辑状态管理
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [editingAction, setEditingAction] = useState<string>('');
    
    // GraphQL Mutation
    const [batchSetNextAction, { loading: setActionLoading }] = useMutation<BatchSetNextActionResponse, BatchSetNextActionVariables>(BATCH_SET_NEXT_ACTION);
    const { data: hubListData } = useQuery<GetHubListResponse>(GET_HUB_LIST);
    const { data: hubWarehouseData } = useQuery<WarehousesResponse>(GET_ALL_WAREHOUSES);

    // 添加反向映射：英文名称 -> 数字值
    const REVERSE_ACTION_NAME_MAP: Record<string, string> = {
        'Hold': '1',
        'ReDeliver': '2', 
        'Return': '3',
        'ReSort': '5'
    };

    // 动态生成国际化的下一步动作选项
    const ACTION_OPTIONS = [
        { value: '1', label: t('config.nextAction.hold') },
        { value: '2', label: t('config.nextAction.reDeliver') },
        { value: '3', label: t('config.nextAction.return') },
        { value: '5', label: t('config.nextAction.reSort') }
    ];

    const handleSelectChange = (newSelectedRowKeys: React.Key[], selectedRows: OrderWithAddressType[]) => {
        if (onSelectChange) {
            onSelectChange(newSelectedRowKeys, selectedRows);
        } else {
            setInternalSelectedRowKeys(newSelectedRowKeys);
        }
    };

    const handleShowParcelDetail = (record: OrderWithAddressType) => {
        showParcelDetail(record);
    };

    // Next Action 编辑功能
    const startEdit = (record: OrderWithAddressType) => {
        setEditingKey(record.id);
        // 将英文的 nextAction 转换为对应的数字值
        const actionValue = REVERSE_ACTION_NAME_MAP[record.nextAction || ''] || '';
        setEditingAction(actionValue);
    };

    const saveEdit = async (recordId: string) => {
        // 验证editingAction是否为有效值
        const validActions = ACTION_OPTIONS.map(opt => opt.value);
        if (!editingAction) {
            message.error(t('wms.exceptions.messages.pleaseSelectNextAction'));
            return;
        }
        if (!validActions.includes(editingAction)) {
            message.error(t('wms.exceptions.messages.pleaseSelectValidAction'));
            return;
        }
        
        try {
            // 使用英文 actionName 进行后端请求
            const actionName = ACTION_NAME_MAP[editingAction] || editingAction;
            
            await batchSetNextAction({
                variables: {
                    orderIds: [parseInt(recordId)], // 转换为数字数组
                    actionName: actionName
                },
                // 确保缓存更新
                refetchQueries: ['WarehouseQuery']
            });
            
            // 获取国际化的动作名称用于显示
            const localizedActionName = getNextActionTextFromBackend(actionName);
            message.success(t('wms.exceptions.messages.nextActionUpdatedSuccessfully', { action: localizedActionName }));
            setEditingKey(null);
            setEditingAction('');

            // 刷新数据
            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            console.error('Failed to update next action:', error);
            showError(error);
        }
    };

    // 异常状态颜色映射
    const EXCEPTION_STATUS_COLOR: Record<number, string> = { 10: 'red', 50: 'orange', 90: 'green' };

    // 获取异常状态显示（使用异常单三态枚举）
    const getExceptionStatus = (record: OrderWithAddressType) => {
        const exceptionStatus = record.exceptionStatus;
        if (exceptionStatus != null) {
            const name = exceptionStatusMap[exceptionStatus];
            if (name) {
                return { status: t(`central.exceptions.exceptionStatus.${name}`), color: EXCEPTION_STATUS_COLOR[exceptionStatus] || 'default' };
            }
        }
        // 兜底
        if (record.isAbnormal) return { status: t('config.exceptionStatus.open'), color: 'red' };
        return { status: t('config.exceptionStatus.resolved'), color: 'green' };
    };

    // 配置行选择
    const rowSelection = {
        selectedRowKeys,
        onChange: handleSelectChange,
    };

    // 直接定义列配置
    const exceptionColumns: TableColumnsType<OrderWithAddressType> = [
        {
            title: t('wms.exceptions.columns.tracking'),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            align: 'left',
            width: 140,
            ellipsis: true,
            render: (text, record) => (
                <span 
                    style={{ 
                        cursor: 'pointer',
                        color: '#722ed1',
                        whiteSpace: 'nowrap'
                    }}
                    onClick={() => handleShowParcelDetail(record)}>
                    {text}
                </span>
            ),
        },
        {
            title: t('wms.exceptions.columns.client'),
            dataIndex: 'channelId',
            key: 'channelId',
            align: 'left',
            width: 120,
            ellipsis: true,
            render: (text, record) => {
                const channel = data?.getAllChannels?.find(
                    (channel) => String(channel.id) === String(record.channelId)
                );
                return <span style={{ whiteSpace: 'nowrap' }}>{channel ? channel.channelName : '-'}</span>;
            },
        },
        {
            title: t('wms.exceptions.columns.hub'),
            dataIndex: 'exceptionHubId',
            key: 'exceptionHubId',
            align: 'left',
            width: 120,
            ellipsis: true,
            render: (text, record) => {
                const hub = hubListData?.hubList.find(it => String(it.id) === String(record.exceptionHubId))
                return <span style={{ whiteSpace: 'nowrap' }}>{hub ? hub.name : '-'}</span>;
            },
        },
        {
            title: t('wms.exceptions.columns.whs'),
            dataIndex: 'exceptionWarehouseId',
            key: 'exceptionWarehouseId',
            align: 'left',
            width: 120,
            ellipsis: true,
            render: (text, record) => {
                const warehouse = hubWarehouseData?.warehouses.filter(it => it.siteType === SiteType.WAREHOUSE).find(it => String(it.id) === String(record.exceptionWarehouseId))
                return <span style={{ whiteSpace: 'nowrap' }}>{warehouse ? warehouse.name : t('wms.exceptions.filters.na')}</span>;
            },
        },
        {
            title: t('wms.exceptions.columns.exceptionReason'),
            dataIndex: 'exceptionReasonCode',
            key: 'exceptionReasonCode',
            align: 'left',
            width: 180,
            ellipsis: true,
            render: (text, record) => {
                const exception = exceptionList?.find(
                    (exception: ExceptionDictionaryType) => String(exception.exceptionReasonCode) === String(record.exceptionReasonCode)
                );
                // 🔧 改进容错性和显示逻辑，参考ParcelColumns的实现
                if (!exception) return <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>;
                
                return <span style={{ whiteSpace: 'nowrap' }}>{getExceptionReasonText(exception, exception.exceptionReasonCode)}</span>;
            }
        },
        {
            title: t('wms.exceptions.columns.exceptionTime'),
            dataIndex: 'markExceptionAt',
            key: 'markExceptionAt',
            align: 'left',
            width: 160,
            ellipsis: true,
            render: (text) => <span style={{ whiteSpace: 'nowrap' }}>{text ? formatTimestampToDateTime(text) : '-'}</span>,
        },
        {
            title: t('wms.exceptions.columns.parcelStatus'),
            dataIndex: 'overallStatus',
            key: 'overallStatus',
            align: 'left',
            width: 120,
            ellipsis: true,
            render: (status) => <span style={{ whiteSpace: 'nowrap' }}>{getStatusText(status) || '-'}</span>,
        },
        {
            title: t('wms.exceptions.columns.deliveryAttempts'),
            dataIndex: 'deliveryAttempts',
            key: 'deliveryAttempts',
            align: 'left',
            width: 100,
            ellipsis: true,
            render: (attempts) => (
                <span className={attempts >= 3 ? 'text-red-600 font-bold' : ''} style={{ whiteSpace: 'nowrap' }}>
                    {attempts || 0}
                </span>
            ),
        },
        {
            title: t('wms.exceptions.columns.receiverAddr'),
            key: 'receiverAddress',
            align: 'left',
            width: 200,
            ellipsis: true,
            render: (_, record) => {
                // 优先显示完整地址，如果没有则显示邮编
                const address = record.receiverFullAddress || record.receiverZipcode || t('wms.exceptions.filters.na');
                return <span style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{address}</span>;
            },
        },
        {
            title: t('wms.exceptions.columns.locationNo'),
            dataIndex: 'warehouseLocationCode',
            key: 'warehouseLocationCode',
            align: 'left',
            width: 120,
            ellipsis: true,
            render: (text: string | null) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>,
        },
        {
            title: t('wms.exceptions.columns.storageNumber'),
            dataIndex: 'storageNumber',
            key: 'storageNumber',
            align: 'left',
            width: 100,
            ellipsis: true,
            render: (storageNumber: number | null) => <span style={{ whiteSpace: 'nowrap' }}>{storageNumber !== null && storageNumber !== undefined ? String(storageNumber).padStart(4, '0') : '-'}</span>,
        },
        {
            title: t('wms.exceptions.columns.exceptionStatus'),
            key: 'exceptionStatus',
            align: 'left',
            width: 120,
            ellipsis: true,
            render: (_, record) => {
                const {status, color} = getExceptionStatus(record);
                return <Tag color={color} style={{ whiteSpace: 'nowrap' }}>{status}</Tag>;
            },
        },
        {
            title: t('wms.exceptions.columns.nextAction'),
            dataIndex: 'nextAction',
            key: 'action_edit',
            align: 'left',
            width: 180,
            fixed: 'right',
            render: (value, record) => {
                if (!record.isAbnormal) return <span style={{ whiteSpace: 'nowrap' }}>-</span>;
                
                // 检查是否为赔付异常，不允许编辑
                const isCompensate = record.nextAction === 'Compensate';
                const canEdit = !isCompensate;
                
                return editingKey === record.id ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Select
                            value={editingAction}
                            onChange={setEditingAction}
                            options={ACTION_OPTIONS}
                            style={{ width: 120 }}
                            allowClear={false}
                            placeholder={t('components.input.select')}
                            size="small"
                        />
                        <div style={{ display: 'flex', gap: 4 }}>
                            <Button
                                icon={<CheckOutlined />}
                                type="text"
                                size="small"
                                style={{ color: '#722ed1', background: 'transparent', padding: '4px' }}
                                loading={setActionLoading && editingKey === record.id}
                                onClick={() => saveEdit(record.id)}
                            />
                            <Button
                                icon={<CloseOutlined />}
                                type="text"
                                size="small"
                                style={{ color: '#9254de', padding: '4px' }}
                                onClick={() => setEditingKey(null)}
                            />
                        </div>
                    </div>
                ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ whiteSpace: 'nowrap' }}>{record.nextAction ? getNextActionTextFromBackend(record.nextAction) : '-'}</span>
                        {canEdit && (
                            <Button
                                icon={<EditOutlined />}
                                type="text"
                                size="small"
                                onClick={() => startEdit(record)}
                            />
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <Table
            rowSelection={rowSelection}
            rowKey="trackingNumber"
            columns={exceptionColumns}
            dataSource={parcelData}
            scroll={{x: 'max-content'}}
            size="middle"
            pagination={false}
            loading={loading}
            tableLayout="fixed"
         className="mb-4"/>
    );
}; 